import React, { ReactNode, HTMLAttributes } from "react";
import { cn } from "../../styles";
import { mdiClose, mdiDrag } from "@mdi/js";
import { Button, ButtonVariant, Icon } from "../../atoms";
import { Panel } from "../../layouts";
import { BaseElementProps, ThemeColor, PanelColor } from "../../props";

export interface DragItemProps extends BaseElementProps {
  /**
   * Content to display inside the drag item.
   * Typically contains the main content that represents the draggable item.
   */
  children?: ReactNode;

  /**
   * Props to be spread onto the drag handle element.
   * Usually contains drag-and-drop library specific props (e.g., react-beautiful-dnd's dragHandleProps).
   * Set to null to hide the drag handle entirely.
   * @default null
   */
  handleProps?: HTMLAttributes<HTMLDivElement> | null;

  /**
   * Callback function called when the action button is clicked.
   * When provided, displays a close/remove button on the right side of the item.
   * Typically used for removing or deleting the drag item.
   */
  onAction?: () => void;

  /**
   * Additional props to customize the action button.
   * Allows overriding default button properties like color, variant, or className.
   */
  buttonProps?: React.ComponentProps<typeof Button>;

  /**
   * Indicates whether the item is currently being dragged.
   * When true, applies enhanced shadow styling to provide visual feedback during drag operations.
   * @default false
   */
  isDragging?: boolean;

  /**
   * Color theme for the panel background.
   * @default PanelColor.level1
   */
  color?: ThemeColor | PanelColor;
}

/**
 * DragItem component for creating draggable list items with optional action buttons.
 * Displays a styled container with an optional drag handle on the left and an optional
 * action button (typically close/remove) on the right. Designed to work seamlessly with
 * the DragList component.
 *
 * The component automatically adjusts its layout based on the presence of drag handles
 * and action buttons, providing visual separators and appropriate spacing. Enhanced
 * shadow effects are applied during drag operations for better user feedback.
 */
export const DragItem = ({
  className,
  children,
  handleProps = null,
  onAction,
  buttonProps,
  isDragging = false,
  dense = false,
  color = PanelColor.level2,
}: DragItemProps) => {
  return (
    <Panel
      color={color}
      rounded={true}
      shadow={isDragging}
      border={true}
      className={cn(
        "flex items-stretch p-0",
        isDragging && "border-primary-300 dark:border-primary-600",
        className
      )}
    >
      {handleProps && (
        <div
          className={cn(
            "flex items-center border-r border-current/10",
            dense ? "px-1 py-0.5" : "px-2 py-1"
          )}
          {...handleProps}
        >
          <Icon size={18} path={mdiDrag} color={ThemeColor.gray} />
        </div>
      )}
      <div className={cn("flex-1 min-w-0", dense ? "px-2 py-1" : "px-3 py-2")}>
        {children}
      </div>
      {onAction && (
        <div className="flex items-center border-l border-current/10">
          <Button
            dense
            iconPath={mdiClose}
            variant={ButtonVariant.text}
            color={ThemeColor.none}
            muted
            onClick={onAction}
            {...buttonProps}
            className={cn(
              "h-full rounded-l-none rounded-r-lg",
              buttonProps?.className
            )}
          />
        </div>
      )}
    </Panel>
  );
};

DragItem.displayName = "DragItem";
